import styled from "styled-components";
import { breakpoint } from "@utilities";
import { AppModuleLayout } from "@layouts";
import { useEffect } from "react";

export const PrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);
    return (
        <AppModuleLayout>
            <StyledPrivacyPolicy>
                <div className="policyWrapper">
                    <div className="policyContent">
                        <h2 className="heading">Privacy Policy</h2>
                        <br />
                        <p className="contentText">
                            In this document, “Freelance Travel”, “we”, “our”, or “us” refers to
                            Paradise Lost Adventure Travel Pty Ltd (ABN 17 670 816 770) trading as
                            Freelance Travel. We are responsible for your personal data. Our contact
                            details are provided at the end of this notice.
                        </p>
                        <p className="contentText">
                            Our Data Protection Policy ensures we treat information from agents,
                            operators, employees, customers, stakeholders, and others with utmost
                            care and confidentiality. We gather, store, and handle data fairly,
                            transparently, and in compliance with the EU General Data Protection
                            Regulation (EU GDPR), Australian Privacy Principles (APP), and other
                            applicable data protection laws.
                        </p>
                        <h3 className="subSectionHeading">Scope</h3>
                        <p className="contentText">
                            This policy applies primarily to travel agents and suppliers but also to
                            our employees, job candidates, and other parties providing data to us.
                        </p>
                        <h3 className="subSectionHeading">
                            Who Is Covered Under the Data Protection Policy
                        </h3>
                        <p className="contentText">
                            This policy covers employees of Freelance Travel, its subsidiaries, and
                            associated entities, as well as contractors, consultants, partners, and
                            external entities collaborating with us or acting on our behalf with
                            lawful access to our data.
                        </p>
                        <h3 className="subSectionHeading">Policy Elements</h3>
                        <p className="contentText">
                            Our core business involves receiving, processing, and retaining data
                            from agents, suppliers, booking systems, and their customers to
                            facilitate travel-related contracts. This includes identifiable data
                            such as names, addresses, usernames, passwords, digital footprints,
                            photographs, financial data, and personal identifiers like social
                            security or driver’s license numbers.
                        </p>
                        <p className="contentText">
                            We collect this data transparently, with the full cooperation and
                            knowledge of agents, suppliers, customers, and other parties. Our data
                            will be:
                            <ul>
                                <li>Accurate and kept up to date</li>
                                <li>Collected fairly and for lawful purposes only</li>
                                <li>Processed within legal and moral boundaries</li>
                                <li>Protected against unauthorized or illegal access</li>
                            </ul>
                        </p>
                        <p className="contentText">
                            Our data will not be:
                            <ul>
                                <li>Communicated informally</li>
                                <li>Stored longer than necessary</li>
                                <li>
                                    Transferred to organizations or countries without adequate data
                                    protection policies
                                </li>
                                <li>Distributed to parties not agreed upon by the data’s owner</li>
                            </ul>
                        </p>
                        <p className="contentText">
                            We have direct obligations to individuals, including:
                            <ul>
                                <li>Informing them which data is collected</li>
                                <li>Explaining how their data is processed</li>
                                <li>Disclosing who has access to their data</li>
                                <li>Having provisions for lost, corrupted, or compromised data</li>
                                <li>
                                    Allowing requests to modify, erase, reduce, or correct data in
                                    our databases
                                </li>
                            </ul>
                        </p>
                        <h3 className="subSectionHeading">Actions</h3>
                        <p className="contentText">
                            To ensure data protection, we commit to:
                            <ul>
                                <li>Restricting and monitoring access to sensitive data</li>
                                <li>Developing transparent data collection procedures</li>
                                <li>Training employees in online privacy and security measures</li>
                                <li>Building secure networks to protect data from cyberattacks</li>
                                <li>
                                    Establishing procedures for reporting privacy breaches or data
                                    misuse
                                </li>
                                <li>Including contract clauses or statements on data handling</li>
                                <li>
                                    Requiring agents, suppliers, and booking systems to comply with
                                    APP and EU GDPR
                                </li>
                                <li>
                                    Implementing data protection practices (e.g., document
                                    shredding, secure locks, data encryption, frequent backups,
                                    access authorization)
                                </li>
                            </ul>
                        </p>
                        <h3 className="subSectionHeading">Disciplinary Consequences</h3>
                        <p className="contentText">
                            All principles in this policy must be strictly followed. Breaches of
                            data protection guidelines are treated seriously and may result in
                            disciplinary or legal action.
                        </p>
                        <h3 className="subSectionHeading">Contact Us</h3>
                        <p className="contentText">
                            For questions about this Privacy Policy, contact us at
                            privacy@freelancetravel.com.
                        </p>
                    </div>
                </div>
            </StyledPrivacyPolicy>
        </AppModuleLayout>
    );
};

const StyledPrivacyPolicy = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    height: calc(100vh - 164px);
    width: 100%;
    padding: 24px;
    box-sizing: border-box;
    background: #fff;

    .policyWrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: auto;
        max-width: 1200px;
        width: 100%;
        padding: 0 24px;
        padding-bottom: 50px;
    }

    .policyContent {
        width: 100%;
        max-width: 1200px;
    }

    .heading {
        font-size: 24px;
        font-weight: 600;
        line-height: 120%;
        color: ${(props) => props.theme.colors.black};
        margin: 0;
        text-align: left;
    }

    .subSectionHeading {
        font-size: 18px;
        font-weight: 600;
        line-height: 120%;
        color: #131214;
        margin: 16px 0 8px 0;
    }

    .contentText {
        margin: 0 0 16px 0;

        ul {
            list-style-type: disc;
            margin: 8px 0 16px 20px;
            padding-left: 20px;
            list-style-position: outside;
        }

        li {
            margin-bottom: 8px;
            font-size: inherit;
            color: ${(props) => props.theme.colors.black};
        }
    }

    ${breakpoint.breakMobileMedium`
        .policyContent {
            padding: 24px;
            font-size: 16px;
            font-weight: 400;

            &::-webkit-scrollbar {
                width: 5px;
            }

            &::-webkit-scrollbar-thumb {
                background-color: rgba(0, 0, 0, .3);
                border-radius: 5px;
            }
        }
    `}

    @media (max-width: ${(p) => p.theme.breakPoints.breakMobile}) {
        .heading {
            font-size: 24px;
        }
        .subHeading,
        .policyContent,
        .checkBoxText {
            font-size: 12px;
        }
        .subSectionHeading {
            font-size: 16px;
        }
        .contentText {
            ul {
                margin: 8px 0 16px 15px;
                padding-left: 15px;
            }
            li {
                font-size: 12px;
            }
        }
    }
`;
